import { useRef, useState } from 'react';
import { 
  Play, 
  Pause, 
  Square, 
  RefreshCw, 
  AlertTriangle, 
  Settings,
  MoreVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  useResumeCampaign,
  useRetryFailedJobs
} from '@/hooks/useEmailBackend';
import {
  isMailingAddressGateError,
  useHermesCampaign,
  useStartDraftCampaign,
  useUpdateHermesCampaign
} from '@/hooks/useHermesCampaigns';
import { useEmulationGuard } from '@/hooks/useEmulationGuard';
import { useReconnectErrorToast } from '@/lib/email/useReconnectErrorToast';
import { MailingAddressRequiredDialog } from '@/components/settings/MailingAddressRequiredDialog';

interface CampaignControlsProps {
  campaignId: string;
  variant?: 'full' | 'compact' | 'minimal';
  className?: string;
  /**
   * Whether to render the status badge. Defaults to true (e.g. the campaign
   * detail page, where these controls own the status display). The list/grid
   * cards render their own status badge, so they pass `false` to avoid showing
   * two badges per card.
   */
  showStatus?: boolean;
}

export function CampaignControls({
  campaignId,
  variant = 'full',
  className = '',
  showStatus = true
}: CampaignControlsProps) {
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState('');

  const { data: campaign } = useHermesCampaign(campaignId);
  const updateCampaign = useUpdateHermesCampaign();
  const resumeCampaign = useResumeCampaign();
  const retryJobs = useRetryFailedJobs();
  const startCampaign = useStartDraftCampaign();
  const { blockIfEmulating } = useEmulationGuard();
  const showReconnectErrorToast = useReconnectErrorToast();
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [mailingAddressOpen, setMailingAddressOpen] = useState(false);
  // Synchronous in-flight guard: isPending only disables the button after a re-render,
  // so a fast double-click would otherwise fire two RPCs.
  const startingRef = useRef(false);
  // Hides Start after success until the refetch flips the cached status off 'draft'.
  const [started, setStarted] = useState(false);

  if (!campaign) {
    return (
      <div className="animate-pulse bg-gray-200 rounded h-10 w-32"></div>
    );
  }

  const handlePause = () => {
    // Only the `full` variant renders the pause confirmation dialog. The
    // `minimal` and `compact` variants (used on the campaign list cards) have no
    // dialog in their markup, so opening it there was a silent no-op — those
    // pause immediately via the hermes update instead.
    if (variant === 'full') {
      setPauseDialogOpen(true);
    } else {
      updateCampaign.mutate({
        campaignId,
        updates: { status: 'paused' }
      });
    }
  };

  const handlePauseConfirm = () => {
    // Use hermes update instead of FastAPI
    updateCampaign.mutate({ 
      campaignId, 
      updates: { status: 'paused' }
    });
    setPauseReason('');
    setPauseDialogOpen(false);
  };

  const handleResume = () => {
    // Route through resumeCampaign so the campaign's pending jobs are re-paced
    // from now (behind the active queue) instead of replaying stale times.
    resumeCampaign.mutate(campaignId);
  };

  const handleStart = () => {
    if (blockIfEmulating('Starting an email campaign')) return;
    setStartDialogOpen(true);
  };

  const handleStartConfirm = async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    try {
      await startCampaign.mutateAsync(campaignId);
      setStarted(true);
    } catch (err) {
      // Generic failures are toasted by the hook with the real message; these two are
      // user-correctable and get their own UI.
      if (isMailingAddressGateError(err)) setMailingAddressOpen(true);
      else showReconnectErrorToast(err);
    } finally {
      startingRef.current = false;
      setStartDialogOpen(false);
    }
  };

  const handleRetryFailed = () => {
    retryJobs.mutate(campaignId);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sending': return 'default';
      case 'completed': return 'secondary';
      case 'paused': return 'outline';
      case 'failed': return 'destructive';
      case 'draft': return 'secondary';
      case 'active': return 'default';
      default: return 'secondary';
    }
  };

  const canPause = ['sending', 'active'].includes(campaign.status);
  const canResume = campaign.status === 'paused';
  const canStart = campaign.status === 'draft' && !started;
  const recipients = campaign.recipient_count;

  // Rendered by every variant: Start confirmation + the CAN-SPAM address gate.
  const startDialogs = (
    <>
      <AlertDialog open={startDialogOpen} onOpenChange={(open) => { if (!startCampaign.isPending) setStartDialogOpen(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start campaign</AlertDialogTitle>
            <AlertDialogDescription>
              Start this campaign?{recipients != null ? ` Up to ${recipients} recipient${recipients === 1 ? '' : 's'};` : ''}{' '}
              emails go out on the account's normal pacing, after any campaign already sending
              from this account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={startCampaign.isPending}>Cancel</AlertDialogCancel>
            {/* Plain Button, not AlertDialogAction: that closes on click, before the outcome is known. */}
            <Button onClick={handleStartConfirm} disabled={startCampaign.isPending}>
              {startCampaign.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Start sending
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <MailingAddressRequiredDialog
        open={mailingAddressOpen}
        onOpenChange={setMailingAddressOpen}
        workspaceId={campaign.workspace_id}
        onSaved={() => {
          setMailingAddressOpen(false);
          void handleStartConfirm();
        }}
        onCancel={() => setMailingAddressOpen(false)}
      />
    </>
  );
  const canRetry = (campaign.emails_failed || 0) > 0;
  const hasWarnings = false; // No warnings for hermes campaigns yet

  if (variant === 'minimal') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {showStatus && (
          <Badge variant={getStatusColor(campaign.status)}>
            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
          </Badge>
        )}
        {canPause && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePause}
            disabled={updateCampaign.isPending}
          >
            <Pause className="h-4 w-4" />
          </Button>
        )}
        {canResume && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResume}
            disabled={resumeCampaign.isPending}
          >
            <Play className="h-4 w-4" />
          </Button>
        )}
        {canStart && (
          <Button
            variant="ghost"
            size="sm"
            aria-label="Start campaign"
            onClick={handleStart}
            disabled={startCampaign.isPending}
          >
            <Play className="h-4 w-4" />
          </Button>
        )}
        {startDialogs}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {showStatus && (
          <Badge variant={getStatusColor(campaign.status)}>
            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
          </Badge>
        )}

        <div className="flex items-center gap-1">
          {canPause && (
            <Button
              variant="outline"
              size="sm"
              onClick={handlePause}
              disabled={updateCampaign.isPending}
            >
              <Pause className="h-4 w-4 mr-1" />
              Pause
            </Button>
          )}
          
          {canResume && (
            <Button
              size="sm"
              onClick={handleResume}
              disabled={resumeCampaign.isPending}
            >
              <Play className="h-4 w-4 mr-1" />
              Resume
            </Button>
          )}

          {canStart && (
            <Button
              size="sm"
              onClick={handleStart}
              disabled={startCampaign.isPending}
            >
              <Play className="h-4 w-4 mr-1" />
              Start
            </Button>
          )}

          {canRetry && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetryFailed}
              disabled={retryJobs.isPending}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry ({campaign.emails_failed || 0})
            </Button>
          )}
        </div>

        {hasWarnings && (
          <Badge variant="destructive" className="text-xs">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Warnings
          </Badge>
        )}
        {startDialogs}
      </div>
    );
  }

  // Full variant
  return (
    <>
      <div className={`flex items-center justify-between ${className}`}>
        <div className="flex items-center gap-4">
          <Badge variant={getStatusColor(campaign.status)} className="text-sm">
            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
          </Badge>
          
          {hasWarnings && (
            <Badge variant="destructive" className="text-sm">
              <AlertTriangle className="h-4 w-4 mr-1" />
              Warnings
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          {canPause && (
            <Button
              variant="outline"
              onClick={handlePause}
              disabled={updateCampaign.isPending}
            >
              <Pause className="h-4 w-4 mr-2" />
              Pause Campaign
            </Button>
          )}

          {canResume && (
            <Button
              onClick={handleResume}
              disabled={resumeCampaign.isPending}
            >
              <Play className="h-4 w-4 mr-2" />
              Resume Campaign
            </Button>
          )}

          {canStart && (
            <Button
              onClick={handleStart}
              disabled={startCampaign.isPending}
            >
              <Play className="h-4 w-4 mr-2" />
              Start Campaign
            </Button>
          )}

          {canRetry && (
            <Button
              variant="outline"
              onClick={handleRetryFailed}
              disabled={retryJobs.isPending}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry Failed ({campaign.emails_failed || 0})
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <Settings className="h-4 w-4 mr-2" />
                Campaign Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600">
                <Square className="h-4 w-4 mr-2" />
                Stop Campaign
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Pause Confirmation Dialog */}
      <Dialog open={pauseDialogOpen} onOpenChange={setPauseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pause Campaign</DialogTitle>
            <DialogDescription>
              This will temporarily halt all email sending for this campaign. 
              You can resume it at any time.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="pause-reason">Reason for pausing (optional)</Label>
              <Textarea
                id="pause-reason"
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder="Enter a reason for pausing this campaign..."
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPauseDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePauseConfirm}
              disabled={updateCampaign.isPending}
            >
              {updateCampaign.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Pausing...
                </>
              ) : (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause Campaign
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {startDialogs}
    </>
  );
}